/**
 * Care Concierge queries and mutations (Feature 106).
 *
 * Sending an invitation moves the sponsored balance, so both mutations invalidate the credit
 * query as well as the engagement list — otherwise the card keeps showing a remaining count that
 * the advisor's own action has already spent.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCareConciergeCase,
  fetchCareConciergeCredit,
  fetchCareConciergeCases,
  requestSessionReturn,
  resendCareConciergeInvitation,
  type CreateCasePayload,
} from '../api/careConciergeApi';

export const careConciergeKeys = {
  credit: ['advisor', 'care-concierge', 'credit'] as const,
  cases: ['advisor', 'care-concierge', 'cases'] as const,
};

export function useCareConciergeCredit() {
  return useQuery({
    queryKey: careConciergeKeys.credit,
    queryFn: fetchCareConciergeCredit,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCareConciergeCases() {
  return useQuery({
    queryKey: careConciergeKeys.cases,
    queryFn: fetchCareConciergeCases,
    staleTime: 1000 * 60 * 5,
  });
}

export function useSendCareConciergeInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateCasePayload) => createCareConciergeCase(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: careConciergeKeys.cases });
      void queryClient.invalidateQueries({ queryKey: careConciergeKeys.credit });
    },
  });
}

/**
 * Invalidates only the engagement list. The credit query is deliberately untouched: opening a
 * request does not return the session, so refreshing the balance here would be showing a change
 * that has not happened.
 */
export function useRequestSessionReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, reason }: { caseId: string; reason?: string }) =>
      requestSessionReturn(caseId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: careConciergeKeys.cases });
    },
  });
}

export function useResendCareConciergeInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (caseId: string) => resendCareConciergeInvitation(caseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: careConciergeKeys.cases });
    },
  });
}
